import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Skeleton } from '../../components/ui/skeleton';
import {
  FileText, RefreshCw, CheckCircle2, XCircle, Clock, Loader2, Zap, ScanLine,
} from 'lucide-react';
import ReprocessMenu from '../shared/ReprocessMenu';

const fmtDate = (v) => {
  if (!v) return '\u2014';
  const d = new Date(v);
  if (isNaN(d)) return v;
  return d.toLocaleDateString('en-US', { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' });
};

const statusCfg = {
  queued: { label: 'Queued', icon: Clock, cls: 'bg-slate-100 text-slate-700 border-slate-200' },
  processing: { label: 'Processing', icon: Loader2, cls: 'bg-blue-100 text-blue-700 border-blue-200', spin: true },
  ocr_extracting: { label: 'OCR', icon: ScanLine, cls: 'bg-amber-100 text-amber-700 border-amber-200', spin: true },
  ai_parsing: { label: 'AI Parsing', icon: Zap, cls: 'bg-purple-100 text-purple-700 border-purple-200', spin: true },
  mysql_matching: { label: 'Matching', icon: RefreshCw, cls: 'bg-cyan-100 text-cyan-700 border-cyan-200', spin: true },
  completed: { label: 'Completed', icon: CheckCircle2, cls: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  failed: { label: 'Failed', icon: XCircle, cls: 'bg-red-100 text-red-700 border-red-200' },
};

function StatusBadge({ status }) {
  const c = statusCfg[status] || statusCfg.queued;
  const Icon = c.icon;
  return <Badge variant="outline" className={`text-[10px] font-semibold ${c.cls} h-5 px-2 gap-1`}><Icon className={`h-3 w-3 ${c.spin ? 'animate-spin' : ''}`} />{c.label}</Badge>;
}

export default function Bill2RollinCockpit() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [filterAccount, setFilterAccount] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [suspects, setSuspects] = useState({ rows: [], total: 0, unique_jobs: 0 });
  const [scanLoading, setScanLoading] = useState(false);
  const [bulkRerunLoading, setBulkRerunLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const toggleSelect = (jobId) => {
    setSelectedIds(prev => {
      const n = new Set(prev);
      if (n.has(jobId)) n.delete(jobId); else n.add(jobId);
      return n;
    });
  };

  const toggleSelectAllVisible = () => {
    const allIds = jobs.map(j => j.job_id);
    const allSelected = allIds.every(id => selectedIds.has(id));
    setSelectedIds(allSelected ? new Set() : new Set(allIds));
  };

  const scanSuspects = useCallback(async () => {
    if (!filterAccount) {
      setSuspects({ rows: [], total: 0, unique_jobs: 0 });
      return;
    }
    setScanLoading(true);
    try {
      const { data } = await api.get('/bill2rollin/suspect-extractions', {
        params: { account_id: filterAccount, limit: 200 },
      });
      setSuspects(data);
    } catch (err) {
      toast.error('Suspect scan failed');
    } finally {
      setScanLoading(false);
    }
  }, [filterAccount]);

  const bulkRerunSuspects = async () => {
    if (!filterAccount) { toast.error('Select an organization first'); return; }
    if (!window.confirm(`Re-run all ${suspects.unique_jobs} jobs with suspect extractions?`)) return;
    setBulkRerunLoading(true);
    try {
      const { data } = await api.post(`/bill2rollin/jobs/bulk-rerun-suspects?account_id=${filterAccount}`);
      toast.success(`Dispatched ${data.dispatched_count} jobs (${data.skipped_count} skipped)`);
      scanSuspects();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Bulk re-run failed');
    } finally {
      setBulkRerunLoading(false);
    }
  };


  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const { data } = await api.get('/super-admin/accounts');
        setAccounts(data || []);
      } catch { /* silent */ }
    };
    fetchAccounts();
  }, []);

  const loadJobs = useCallback(async () => {
    setLoading(true);
    try {
      // Super-admin: fetch jobs for all accounts or a specific one
      const params = { page: 1, limit: 100 };
      if (filterAccount) params.account_id = filterAccount;
      if (filterStatus) params.status = filterStatus;

      // If no specific account, query across all with a global endpoint
      if (!filterAccount && accounts.length > 0) {
        // Fetch from first few accounts for cockpit overview
        const allJobs = [];
        for (const acc of accounts.slice(0, 20)) {
          try {
            const { data } = await api.get('/bill2rollin/jobs', { params: { account_id: acc.id, limit: 10 } });
            for (const j of (data.jobs || [])) {
              j._org_name = acc.organisation_name;
              allJobs.push(j);
            }
          } catch { /* silent */ }
        }
        allJobs.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setJobs(allJobs.slice(0, 50));
      } else if (filterAccount) {
        const { data } = await api.get('/bill2rollin/jobs', { params });
        const acc = accounts.find(a => a.id === filterAccount);
        for (const j of (data.jobs || [])) j._org_name = acc?.organisation_name || '';
        setJobs(data.jobs || []);
      } else {
        setJobs([]);
      }
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [filterAccount, filterStatus, accounts]);

  useEffect(() => { if (accounts.length > 0) loadJobs(); }, [loadJobs, accounts]);

  const rerunJob = async (job) => {
    try {
      await api.post(`/bill2rollin/jobs/${job.job_id}/rerun?account_id=${job.account_id}`);
      toast.success('Job re-queued');
      loadJobs();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Re-run failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="cockpit-bill2rollin">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold">Bill2Rollin Jobs</h2>
        <Button variant="outline" size="sm" onClick={loadJobs}><RefreshCw className="h-3.5 w-3.5 mr-1" /> Refresh</Button>
      </div>

      <div className="flex items-center gap-2">
        <Select value={filterAccount} onValueChange={(v) => setFilterAccount(v === 'all' ? '' : v)}>
          <SelectTrigger className="h-8 w-56 text-xs"><SelectValue placeholder="All Organizations" /></SelectTrigger>
          <SelectContent className="max-h-60">
            <SelectItem value="all">All Organizations</SelectItem>
            {accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.organisation_name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={(v) => setFilterStatus(v === 'all' ? '' : v)}>
          <SelectTrigger className="h-8 w-36 text-xs"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="queued">Queued</SelectItem>
            <SelectItem value="processing">Processing</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
          </SelectContent>
        </Select>
        <span className="ml-auto text-xs text-muted-foreground">{jobs.length} jobs</span>
      </div>

      <div className="border rounded-md overflow-x-auto">
        <Table className="w-full min-w-[1000px]">
          <TableHeader className="sticky top-0 bg-card z-10">
            <TableRow>
              <TableHead className="w-[32px] pl-2">
                <Checkbox
                  checked={jobs.length > 0 && jobs.every(j => selectedIds.has(j.job_id))}
                  onCheckedChange={toggleSelectAllVisible}
                  aria-label="Select all"
                  data-testid="select-all-jobs"
                />
              </TableHead>
              <TableHead className="text-xs font-semibold w-[50px]">#</TableHead>
              <TableHead className="text-xs font-semibold w-[160px]">Organization</TableHead>
              <TableHead className="text-xs font-semibold w-[200px]">File</TableHead>
              <TableHead className="text-xs font-semibold w-[100px]">Status</TableHead>
              <TableHead className="text-xs font-semibold w-[140px]">Property</TableHead>
              <TableHead className="text-xs font-semibold w-[60px] text-center">Accts</TableHead>
              <TableHead className="text-xs font-semibold w-[60px] text-center">Meters</TableHead>
              <TableHead className="text-xs font-semibold w-[70px]">Dispatch</TableHead>
              <TableHead className="text-xs font-semibold w-[100px]">Uploaded By</TableHead>
              <TableHead className="text-xs font-semibold w-[120px]">Date</TableHead>
              <TableHead className="text-xs font-semibold w-[70px] text-center">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => <TableRow key={i}>{Array.from({ length: 12 }).map((_, j) => <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>)}</TableRow>)
            ) : jobs.length === 0 ? (
              <TableRow><TableCell colSpan={12} className="text-center py-12 text-muted-foreground">No Bill2Rollin jobs found.</TableCell></TableRow>
            ) : jobs.map((j, i) => (
              <TableRow key={j.job_id} className="hover:bg-muted/20">
                <TableCell className="pl-2">
                  <Checkbox
                    checked={selectedIds.has(j.job_id)}
                    onCheckedChange={() => toggleSelect(j.job_id)}
                    aria-label="Select job"
                    data-testid={`select-job-${i}`}
                  />
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{i + 1}</TableCell>
                <TableCell className="text-xs font-medium truncate">{j._org_name || '\u2014'}</TableCell>
                <TableCell className="text-xs"><div className="flex items-center gap-1.5"><FileText className="h-3.5 w-3.5 text-red-500 shrink-0" /><span className="truncate">{j.file_name}</span></div></TableCell>
                <TableCell><StatusBadge status={j.status} /></TableCell>
                <TableCell className="text-xs truncate">{j.property_name || '\u2014'}</TableCell>
                <TableCell className="text-xs text-center font-mono">{j.accounts_count ?? '\u2014'}</TableCell>
                <TableCell className="text-xs text-center font-mono">{j.meters_count ?? '\u2014'}</TableCell>
                <TableCell className="text-xs">
                  <Badge variant="outline" className={`text-[9px] h-4 px-1.5 ${j.dispatch === 'sqs' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                    {j.dispatch === 'sqs' ? 'SQS' : 'Local'}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground truncate">{j.uploaded_by || '\u2014'}</TableCell>
                <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(j.created_at)}</TableCell>
                <TableCell className="text-center">
                  {j.status !== 'processing' && j.status !== 'queued' && (
                    <Button variant="outline" size="sm" className="h-6 text-[10px] px-2 gap-1" onClick={() => rerunJob(j)} data-testid={`cockpit-rerun-${i}`}>
                      <RefreshCw className="h-3 w-3" /> Re-run
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {jobs.some(j => j.status === 'failed' && j.error) && (
        <div className="border rounded-lg p-3 bg-red-50 border-red-200">
          <h3 className="text-xs font-semibold text-red-700 mb-2">Recent Errors</h3>
          {jobs.filter(j => j.status === 'failed' && j.error).slice(0, 5).map(j => (
            <div key={j.job_id} className="text-[10px] text-red-600 mb-1 font-mono truncate" title={j.error}>
              {j.file_name}: {j.error}
            </div>
          ))}
        </div>
      )}

      {/* Suspect Extractions Audit */}
      <div className="border rounded-lg bg-card" data-testid="suspect-extractions-card">
        <div className="px-4 py-3 border-b flex items-center justify-between flex-wrap gap-2">
          <div>
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <ScanLine className="h-4 w-4 text-amber-600" />
              Suspect Account-Number Extractions
            </h3>
            <p className="text-[11px] text-muted-foreground mt-0.5">
              Scans existing extractions and flags account_numbers that look like meter readings
              (long digit strings, concatenations, inconsistent across bills for same vendor + address).
              {!filterAccount && ' — pick an organization above to scan.'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={scanSuspects}
              disabled={!filterAccount || scanLoading}
              className="h-7 text-[11px]"
              data-testid="scan-suspects-btn"
            >
              {scanLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <ScanLine className="h-3 w-3 mr-1" />}
              Scan
            </Button>
            {suspects.unique_jobs > 0 && (
              <Button
                variant="default"
                size="sm"
                onClick={bulkRerunSuspects}
                disabled={bulkRerunLoading}
                className="h-7 text-[11px]"
                data-testid="bulk-rerun-suspects-btn"
              >
                {bulkRerunLoading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                Re-run all {suspects.unique_jobs}
              </Button>
            )}
          </div>
        </div>
        {suspects.rows.length === 0 ? (
          <div className="px-4 py-8 text-center text-xs text-muted-foreground">
            {filterAccount && !scanLoading ? 'No suspect extractions found in this organization.' : 'Click Scan to find mis-extracted bills.'}
          </div>
        ) : (
          <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
            <Table className="w-full">
              <TableHeader className="sticky top-0 bg-card z-10">
                <TableRow>
                  <TableHead className="text-[10px] uppercase tracking-wider w-[150px]">Account #</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider w-[140px]">Vendor</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider w-[160px]">Service Address</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider">Why Suspect</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider w-[140px]">File</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {suspects.rows.map((s, i) => (
                  <TableRow key={`${s.id}-${i}`} className="hover:bg-amber-50/40" data-testid={`suspect-row-${i}`}>
                    <TableCell className="font-mono text-[11px] text-red-700 break-all">{s.account_number}</TableCell>
                    <TableCell className="text-[11px]">{s.vendor || '\u2014'}</TableCell>
                    <TableCell className="text-[11px] truncate max-w-[160px]" title={s.service_address}>{s.service_address || '\u2014'}</TableCell>
                    <TableCell className="text-[10px] text-amber-700">
                      <ul className="space-y-0.5 list-disc pl-3">
                        {s.warnings.map((w, wi) => <li key={wi}>{w}</li>)}
                      </ul>
                    </TableCell>
                    <TableCell className="text-[10px] truncate max-w-[140px]" title={s.file_name}>{s.file_name || '\u2014'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <div className="px-3 py-2 text-[10px] text-muted-foreground border-t bg-muted/30">
              {suspects.total} suspects · {suspects.unique_jobs} unique jobs
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
